import React, { useState, useEffect } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  Switch,
  Box,
  Alert,
  AlertIcon,
  Grid,
  GridItem,
  IconButton,
  Badge,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Checkbox,
  CheckboxGroup,
  Stack,
} from '@chakra-ui/react';

import { FaPlus, FaTrash } from 'react-icons/fa';

import { useColors } from './colors';

const ScheduleSettingsModal = ({
  isOpen,
  onClose,
  onSave,
  currentSettings,
  storeOptions = [],
}) => {
  const { textColor, inputColor } = useColors();

  const [schedule, setSchedule] = useState({
    schedule: [
      {
        dayOfWeek: 1, // Monday
        time: '09:00',
        enabled: true,
        storeIds: [],
      },
    ],
    maxListingsPerRun: 25000,
    statusesToRefetch: ['optimized', 'active'],
  });

  // Load current settings when modal opens
  useEffect(() => {
    if (currentSettings && isOpen) {
      const loadedSchedule = currentSettings.schedule || [
        {
          dayOfWeek: 1,
          time: '09:00',
          enabled: true,
          storeIds: [],
        },
      ];
      // Deep clone schedule items so each has its own storeIds array
      const clonedSchedule = loadedSchedule.map((item) => ({
        ...item,
        storeIds: Array.isArray(item.storeIds) ? [...item.storeIds] : [],
      }));
      setSchedule({
        schedule: clonedSchedule,
        maxListingsPerRun: currentSettings.maxListingsPerRun || 25000,
        statusesToRefetch: Array.isArray(currentSettings.statusesToRefetch)
          ? [...currentSettings.statusesToRefetch]
          : ['optimized', 'active'],
      });
    }
  }, [currentSettings, isOpen]);

  const handleSave = () => {
    onSave(schedule);
    onClose();
  };

  const dayOptions = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  const addScheduleItem = () => {
    setSchedule({
      ...schedule,
      schedule: [
        ...schedule.schedule,
        {
          dayOfWeek: 1,
          time: '09:00',
          enabled: true,
          storeIds: [],
        },
      ],
    });
  };

  const removeScheduleItem = (index) => {
    if (schedule.schedule.length > 1) {
      setSchedule({
        ...schedule,
        schedule: schedule.schedule.filter((_, i) => i !== index),
      });
    }
  };

  const updateScheduleItem = (index, field, value) => {
    setSchedule((prev) => {
      const updatedSchedule = prev.schedule.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
      return { ...prev, schedule: updatedSchedule };
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="1000px" maxH="90vh" borderRadius="xl" boxShadow="2xl">
        <ModalHeader color={textColor} fontSize="xl" fontWeight="600" pb={2}>
          Optimization Schedule Settings
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto" maxH="calc(90vh - 140px)">
          <Grid templateColumns="1fr 1fr" gap={8}>
            {/* Left Column - Schedule */}
            <GridItem>
              <VStack spacing={6} align="stretch">
                <Box>
                  <HStack justify="space-between" align="center" mb={4}>
                    <Text fontSize="lg" fontWeight="600" color={textColor}>
                      Schedule Settings
                    </Text>
                    <Button
                      leftIcon={<FaPlus />}
                      size="sm"
                      variant="outline"
                      onClick={addScheduleItem}
                      colorScheme="blue"
                      borderRadius="md"
                      borderColor="blue.200"
                      color="blue.600"
                      _hover={{ bg: 'blue.50', borderColor: 'blue.300' }}
                      fontWeight="500"
                    >
                      Add Schedule
                    </Button>
                  </HStack>

                  <VStack spacing={4} align="stretch" maxH="350px" overflowY="auto">
                    {schedule.schedule.map((item, index) => (
                      <Box
                        key={index}
                        p={5}
                        border="1px solid"
                        borderColor="gray.100"
                        borderRadius="lg"
                        bg="white"
                        boxShadow="sm"
                        _hover={{ borderColor: 'blue.200' }}
                        transition="all 0.2s"
                        mb={4}
                      >
                        <HStack spacing={3} align="end">
                          <FormControl flex={1}>
                            <FormLabel
                              color={textColor}
                              fontSize="sm"
                              fontWeight="500"
                              mb={1}
                            >
                              Day
                            </FormLabel>
                            <Menu>
                              <MenuButton
                                as={Button}
                                bg="white"
                                color={inputColor}
                                borderColor="gray.200"
                                variant="outline"
                                w="100%"
                                size="sm"
                                borderRadius="md"
                                _hover={{ borderColor: 'blue.300' }}
                                _focus={{
                                  borderColor: 'blue.400',
                                  boxShadow: '0 0 0 1px blue.400',
                                }}
                                textAlign="left"
                                justifyContent="flex-start"
                              >
                                {dayOptions.find((opt) => opt.value === item.dayOfWeek)
                                  ?.label || 'Monday'}
                              </MenuButton>
                              <MenuList
                                bg="white"
                                borderColor="gray.200"
                                p={2}
                                minWidth="100%"
                                shadow="lg"
                              >
                                {dayOptions.map((option) => (
                                  <MenuItemOption
                                    key={option.value}
                                    value={option.value}
                                    bg="transparent"
                                    color={inputColor}
                                    _hover={{ bg: 'blue.50' }}
                                    onClick={() =>
                                      updateScheduleItem(index, 'dayOfWeek', option.value)
                                    }
                                  >
                                    {option.label}
                                  </MenuItemOption>
                                ))}
                              </MenuList>
                            </Menu>
                          </FormControl>

                          <FormControl flex={1.2}>
                            <FormLabel
                              color={textColor}
                              fontSize="sm"
                              fontWeight="500"
                              mb={1}
                            >
                              Time
                            </FormLabel>
                            <Input
                              type="time"
                              value={item.time}
                              onChange={(e) =>
                                updateScheduleItem(index, 'time', e.target.value)
                              }
                              color={inputColor}
                              size="sm"
                              borderRadius="md"
                              borderColor="gray.200"
                              _hover={{ borderColor: 'blue.300' }}
                              _focus={{
                                borderColor: 'blue.400',
                                boxShadow: '0 0 0 1px blue.400',
                              }}
                            />
                          </FormControl>

                          <FormControl flex={0.8}>
                            <FormLabel
                              color={textColor}
                              fontSize="sm"
                              fontWeight="500"
                              mb={1}
                            >
                              Active
                            </FormLabel>
                            <Switch
                              isChecked={item.enabled}
                              onChange={(e) =>
                                updateScheduleItem(index, 'enabled', e.target.checked)
                              }
                              colorScheme="blue"
                              size="md"
                            />
                          </FormControl>
                        </HStack>

                        {/* Store Selection */}
                        <Box mt={3}>
                          <FormLabel
                            color={textColor}
                            fontSize="sm"
                            fontWeight="500"
                            mb={2}
                          >
                            Select Stores
                          </FormLabel>
                          <Menu closeOnSelect={false}>
                            <MenuButton
                              as={Button}
                              bg="white"
                              color={textColor}
                              borderColor="gray.200"
                              variant="outline"
                              w="100%"
                              size="sm"
                              borderRadius="md"
                              _hover={{
                                borderColor: 'blue.300',
                              }}
                              _active={{
                                borderColor: 'blue.300',
                              }}
                              _focus={{
                                borderColor: 'blue.400',
                                boxShadow: '0 0 0 1px blue.400',
                              }}
                              textAlign="left"
                              justifyContent="flex-start"
                            >
                              {(item.storeIds || []).length > 0
                                ? `${(item.storeIds || []).length} store${(item.storeIds || []).length > 1 ? 's' : ''} selected`
                                : 'Choose store(s)'}
                            </MenuButton>

                            <MenuList
                              bg="white"
                              borderColor="gray.200"
                              p={3}
                              minWidth="100%"
                              shadow="lg"
                            >
                              <Text
                                fontSize="xs"
                                fontWeight="600"
                                color="gray.500"
                                textTransform="uppercase"
                                mb={2}
                                px={1}
                              >
                                Stores
                              </Text>
                              <VStack align="stretch" spacing={1} maxH="200px" overflowY="auto">
                                {storeOptions.map((store, storeIndex) => {
                                  const storeId = store._id ?? store.id;
                                  const currentIds = item.storeIds || [];
                                  const isChecked = currentIds.some(
                                    (id) => String(id) === String(storeId)
                                  );
                                  return (
                                    <Checkbox
                                      key={storeId ?? `store-${storeIndex}`}
                                      size="sm"
                                      isChecked={isChecked}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        const newIds = isChecked
                                          ? currentIds.filter(
                                              (id) => String(id) !== String(storeId)
                                            )
                                          : [...currentIds, storeId];
                                        updateScheduleItem(index, 'storeIds', newIds);
                                      }}
                                      colorScheme="blue"
                                    >
                                      {store.name}
                                    </Checkbox>
                                  );
                                })}
                              </VStack>
                            </MenuList>
                          </Menu>
                          {item.storeIds && item.storeIds.length > 0 && (
                            <Flex wrap="wrap" gap={1} mt={2}>
                              {item.storeIds.map((storeId) => {
                                const store = storeOptions.find(
                                  (s) =>
                                    String(s._id ?? s.id) === String(storeId)
                                );
                                return store ? (
                                  <Badge
                                    key={storeId}
                                    colorScheme="green"
                                    variant="subtle"
                                    fontSize="xs"
                                    px={2}
                                    py={1}
                                    borderRadius="md"
                                  >
                                    {store.name}
                                  </Badge>
                                ) : null;
                              })}
                            </Flex>
                          )}
                        </Box>

                        {schedule.schedule.length > 1 && (
                          <IconButton
                            icon={<FaTrash />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => removeScheduleItem(index)}
                            aria-label="Remove schedule"
                            mt={6}
                            _hover={{ bg: 'red.50' }}
                          />
                        )}
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </VStack>
            </GridItem>

            {/* Right Column - Max Listings */}
            <GridItem>
              <VStack spacing={8} align="stretch">
                {/* Max Listings */}
                <Box>
                  <Text fontSize="lg" fontWeight="600" color={textColor} mb={4}>
                    Process Settings
                  </Text>

                  <FormControl>
                    <FormLabel color={textColor} fontSize="sm" fontWeight="500" mb={2}>
                      Max Listings Per Run
                    </FormLabel>
                    <NumberInput
                      value={schedule.maxListingsPerRun}
                      onChange={(value) =>
                        setSchedule({ ...schedule, maxListingsPerRun: parseInt(value) })
                      }
                      min={1}
                      max={100000}
                    >
                      <NumberInputField
                        color={inputColor}
                        borderRadius="md"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'blue.300' }}
                        _focus={{
                          borderColor: 'blue.400',
                          boxShadow: '0 0 0 1px blue.400',
                        }}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </Box>

                {/* Re-fetch listings with status (schedule run only) */}
                <Box>
                  <Text fontSize="lg" fontWeight="600" color={textColor} mb={2}>
                    Re-fetch on Schedule
                  </Text>
                  <Text fontSize="sm" color="gray.600" mb={3}>
                    When the schedule runs, which listing statuses to refresh (re-fetch
                    competitor data).
                  </Text>
                  <FormControl>
                    <FormLabel color={textColor} fontSize="sm" fontWeight="500" mb={2}>
                      Re-fetch listings with status
                    </FormLabel>
                    <CheckboxGroup
                      value={schedule.statusesToRefetch || ['optimized', 'active']}
                      onChange={(values) =>
                        setSchedule({ ...schedule, statusesToRefetch: values })
                      }
                    >
                      <Stack spacing={2}>
                        <Checkbox
                          value="optimized"
                          colorScheme="blue"
                          color={textColor}
                          size="md"
                        >
                          Optimized
                        </Checkbox>
                        <Checkbox
                          value="active"
                          colorScheme="blue"
                          color={textColor}
                          size="md"
                        >
                          Active
                        </Checkbox>
                        <Checkbox
                          value="queue"
                          colorScheme="blue"
                          color={textColor}
                          size="md"
                        >
                          Queue
                        </Checkbox>
                      </Stack>
                    </CheckboxGroup>
                  </FormControl>
                </Box>
              </VStack>
            </GridItem>
          </Grid>

          <Alert
            status="info"
            mt={8}
            borderRadius="md"
            bg="blue.50"
            borderColor="blue.200"
          >
            <AlertIcon color="blue.500" />
            <Box>
              <Text fontSize="sm" color="blue.700">
                The optimization process will run automatically based on your schedule
                settings. You can manually trigger the process at any time from the main
                dashboard.
              </Text>
            </Box>
          </Alert>
        </ModalBody>

        <ModalFooter pt={4} borderTop="1px solid" borderColor="gray.100">
          <Button variant="ghost" mr={3} onClick={onClose} fontWeight="500">
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            borderRadius="md"
            fontWeight="500"
            px={6}
          >
            Save Settings
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ScheduleSettingsModal;
